import { useState } from 'react';

interface Props {
  onSignUp: () => void;
}

const PLATFORMS = [
  {
    name: 'Amazon KDP',
    icon: 'ri-amazon-line',
    royalty: '70%',
    desc: 'Reach 300M+ shoppers',
    color: '#FF9900',
    bg: '#FFF8ED',
    border: '#FFE0A3',
    reach: '300M+',
  },
  {
    name: 'Apple Books',
    icon: 'ri-apple-line',
    royalty: '70%',
    desc: '1.5B Apple devices',
    color: '#555555',
    bg: '#F5F5F5',
    border: '#E0E0E0',
    reach: '1.5B',
  },
  {
    name: 'Google Play',
    icon: 'ri-google-play-line',
    royalty: '52%',
    desc: '2B+ Android users',
    color: '#34A853',
    bg: '#F0FBF4',
    border: '#BBF0CC',
    reach: '2B+',
  },
  {
    name: 'Kobo',
    icon: 'ri-book-open-line',
    royalty: '70%',
    desc: '190+ countries',
    color: '#D4001A',
    bg: '#FFF0F2',
    border: '#FFB3BC',
    reach: '190+',
  },
  {
    name: 'Barnes & Noble',
    icon: 'ri-store-2-line',
    royalty: '65%',
    desc: 'US market leader',
    color: '#1B6B3A',
    bg: '#F0F9F4',
    border: '#A3D9B8',
    reach: '29M+',
  },
  {
    name: 'Draft2Digital',
    icon: 'ri-global-line',
    royalty: '60%',
    desc: 'Distribute everywhere',
    color: '#6C5CE7',
    bg: '#F4F0FF',
    border: '#C7B8FF',
    reach: '40+',
  },
  {
    name: 'Smashwords',
    icon: 'ri-draft-line',
    royalty: '85%',
    desc: '60+ retailer network',
    color: '#E74C3C',
    bg: '#FFF0EF',
    border: '#FFBDB8',
    reach: '60+',
  },
  {
    name: 'Your Website',
    icon: 'ri-links-line',
    royalty: '100%',
    desc: 'Keep every dollar',
    color: '#F97316',
    bg: '#FFF7ED',
    border: '#FED7AA',
    reach: '∞',
  },
];

const STEPS = [
  { icon: 'ri-magic-line', label: 'Create', desc: 'Write & design with AI' },
  { icon: 'ri-palette-line', label: 'Design', desc: 'Professional cover & layout' },
  { icon: 'ri-upload-cloud-2-line', label: 'Export', desc: 'PDF, EPUB, DOCX, MOBI' },
  { icon: 'ri-store-2-line', label: 'Publish', desc: 'One-click distribution' },
  { icon: 'ri-money-dollar-circle-line', label: 'Earn', desc: 'Collect royalties instantly' },
];

const EARNINGS_DATA = [
  { label: 'Amazon KDP', pct: 70, amount: '$3.50', color: '#FF9900' },
  { label: 'Apple Books', pct: 70, amount: '$3.50', color: '#555555' },
  { label: 'Kobo', pct: 70, amount: '$3.50', color: '#D4001A' },
  { label: 'Your Website', pct: 100, amount: '$5.00', color: '#F97316' },
];

export default function PublishSellBanner({ onSignUp }: Props) {
  const [activePlatform, setActivePlatform] = useState<string | null>(null);
  const [hovered, setHovered] = useState<string | null>(null);

  return (
    <div className="bg-gradient-to-b from-slate-900 via-slate-800 to-slate-900 text-white overflow-hidden">
      {/* ── Top strip ──────────────────────────────────────────────────────── */}
      <div className="border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-orange-500/20 border border-orange-500/40 rounded-xl flex items-center justify-center">
              <i className="ri-rocket-line text-orange-400 text-base"></i>
            </div>
            <span className="text-sm font-bold text-white">Publish &amp; Sell Your eBook</span>
            <span className="text-xs text-slate-400">— distribute to 8 major platforms in one click</span>
          </div>
          <button
            onClick={onSignUp}
            className="flex items-center gap-2 px-4 py-2 bg-orange-500 hover:bg-orange-400 text-white text-xs font-bold rounded-xl transition-all cursor-pointer whitespace-nowrap"
          >
            <i className="ri-arrow-right-up-line text-sm"></i>
            Start for Free
          </button>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-16">
        {/* ── Headline ────────────────────────────────────────────────────── */}
        <div className="text-center mb-14">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-orange-500/15 border border-orange-500/30 rounded-full mb-5">
            <div className="w-1.5 h-1.5 bg-orange-400 rounded-full animate-pulse"></div>
            <span className="text-xs font-bold text-orange-300 uppercase tracking-wider">Instant Distribution</span>
          </div>
          <h2 className="text-4xl font-extrabold text-white mb-4 leading-tight">
            Write Once.<br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-400 to-amber-300">Sell Everywhere.</span>
          </h2>
          <p className="text-slate-400 text-base max-w-xl mx-auto leading-relaxed">
            Publish your eBook to every major platform simultaneously. Reach millions of readers worldwide and keep up to <strong className="text-white">100% of your royalties</strong>.
          </p>
        </div>

        {/* ── How It Works ────────────────────────────────────────────────── */}
        <div className="flex items-center justify-center gap-0 mb-14 overflow-x-auto pb-2">
          {STEPS.map((step, idx) => (
            <div key={step.label} className="flex items-center">
              <div className="flex flex-col items-center gap-2 px-4 min-w-[90px]">
                <div className="w-12 h-12 rounded-2xl bg-white/10 border border-white/15 flex items-center justify-center">
                  <i className={`${step.icon} text-orange-400 text-xl`}></i>
                </div>
                <span className="text-xs font-extrabold text-white whitespace-nowrap">{step.label}</span>
                <span className="text-xs text-slate-500 text-center whitespace-nowrap">{step.desc}</span>
              </div>
              {idx < STEPS.length - 1 && (
                <div className="flex items-center flex-shrink-0">
                  <div className="w-8 h-px bg-gradient-to-r from-orange-500/60 to-amber-500/30"></div>
                  <i className="ri-arrow-right-s-line text-orange-500/60 text-sm -mx-1"></i>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* ── Platform Cards ──────────────────────────────────────────────── */}
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-3 mb-12">
          {PLATFORMS.map(p => {
            const isActive = activePlatform === p.name;
            const isHovered = hovered === p.name;
            return (
              <button
                key={p.name}
                onClick={() => setActivePlatform(isActive ? null : p.name)}
                onMouseEnter={() => setHovered(p.name)}
                onMouseLeave={() => setHovered(null)}
                className={`group relative rounded-2xl p-3 flex flex-col items-center gap-2 transition-all duration-200 cursor-pointer border text-center ${
                  isActive
                    ? 'border-orange-500/60 bg-white/10 scale-105 shadow-lg shadow-orange-500/10'
                    : 'border-white/10 bg-white/5 hover:bg-white/10 hover:border-white/20 hover:scale-105'
                }`}
              >
                {/* Royalty badge */}
                <div
                  className={`absolute -top-2 -right-2 px-2 py-0.5 rounded-full text-xs font-extrabold border transition-all duration-200 ${
                    isActive || isHovered ? 'opacity-100 scale-100' : 'opacity-0 scale-75'
                  }`}
                  style={{ backgroundColor: p.color, borderColor: p.color + '80', color: 'white' }}
                >
                  {p.royalty}
                </div>

                <div
                  className="w-10 h-10 rounded-xl flex items-center justify-center border transition-colors"
                  style={{
                    backgroundColor: isActive ? p.bg : 'rgba(255,255,255,0.08)',
                    borderColor: isActive ? p.border : 'rgba(255,255,255,0.12)',
                  }}
                >
                  <i
                    className={`${p.icon} text-xl transition-colors`}
                    style={{ color: isActive ? p.color : '#94a3b8' }}
                  ></i>
                </div>
                <div>
                  <p className={`text-xs font-bold whitespace-nowrap transition-colors ${isActive ? 'text-white' : 'text-slate-300'}`}>
                    {p.name}
                  </p>
                  <p className="text-xs text-slate-500 whitespace-nowrap">{p.desc}</p>
                </div>
              </button>
            );
          })}
        </div>

        {/* ── Royalty Breakdown ───────────────────────────────────────────── */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12">
          {/* Left: Earnings chart */}
          <div className="bg-white/5 border border-white/10 rounded-2xl p-6">
            <div className="flex items-center gap-2 mb-5">
              <div className="w-7 h-7 flex items-center justify-center">
                <i className="ri-line-chart-line text-orange-400 text-base"></i>
              </div>
              <div>
                <p className="text-sm font-extrabold text-white">Royalty Per $5 Sale</p>
                <p className="text-xs text-slate-500">Estimated earnings by platform</p>
              </div>
            </div>
            <div className="space-y-3">
              {EARNINGS_DATA.map(item => (
                <div key={item.label} className="flex items-center gap-3">
                  <span className="text-xs text-slate-400 w-24 flex-shrink-0">{item.label}</span>
                  <div className="flex-1 h-6 bg-white/5 rounded-lg overflow-hidden">
                    <div
                      className="h-full rounded-lg flex items-center justify-end pr-2 transition-all duration-700"
                      style={{ width: `${item.pct}%`, backgroundColor: item.color + 'cc' }}
                    >
                      <span className="text-xs font-bold text-white">{item.amount}</span>
                    </div>
                  </div>
                  <span className="text-xs font-bold w-10 text-right flex-shrink-0" style={{ color: item.color }}>
                    {item.pct}%
                  </span>
                </div>
              ))}
            </div>
            <p className="text-xs text-slate-600 mt-4">* Approximate royalties on a $4.99 eBook price. Rates may vary.</p>
          </div>

          {/* Right: Key features */}
          <div className="bg-white/5 border border-white/10 rounded-2xl p-6">
            <div className="flex items-center gap-2 mb-5">
              <div className="w-7 h-7 flex items-center justify-center">
                <i className="ri-rocket-line text-amber-400 text-base"></i>
              </div>
              <div>
                <p className="text-sm font-extrabold text-white">Why Sell with openclaw.ai</p>
                <p className="text-xs text-slate-500">Everything you need, in one place</p>
              </div>
            </div>
            <div className="space-y-3">
              {[
                { icon: 'ri-timer-flash-line', color: 'text-orange-400', text: 'Publish to all platforms in under 5 minutes' },
                { icon: 'ri-money-dollar-circle-line', color: 'text-emerald-400', text: 'Collect royalties directly — zero hidden fees' },
                { icon: 'ri-bar-chart-2-line', color: 'text-amber-400', text: 'Unified analytics dashboard across all channels' },
                { icon: 'ri-translate-2', color: 'text-sky-400', text: 'Auto-translate to 50+ languages before publishing' },
                { icon: 'ri-shield-check-line', color: 'text-violet-400', text: 'DRM protection and piracy monitoring built in' },
                { icon: 'ri-price-tag-3-line', color: 'text-rose-400', text: 'Dynamic pricing — adjust per market automatically' },
              ].map((item, i) => (
                <div key={i} className="flex items-center gap-3">
                  <div className="w-7 h-7 flex items-center justify-center rounded-lg bg-white/8 flex-shrink-0">
                    <i className={`${item.icon} ${item.color} text-sm`}></i>
                  </div>
                  <span className="text-sm text-slate-300">{item.text}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* ── CTA Block ───────────────────────────────────────────────────── */}
        <div className="relative rounded-3xl overflow-hidden border border-orange-500/30">
          <div
            className="absolute inset-0"
            style={{
              background: 'radial-gradient(ellipse at 60% 50%, rgba(249,115,22,0.18) 0%, rgba(245,158,11,0.08) 50%, transparent 80%)',
            }}
          />
          <div className="relative px-10 py-10 flex flex-col md:flex-row items-center justify-between gap-8">
            {/* Left copy */}
            <div>
              <p className="text-xs font-bold text-orange-400 uppercase tracking-widest mb-2">Start publishing today</p>
              <h3 className="text-2xl font-extrabold text-white mb-2 leading-tight">
                Your first eBook.<br />8 platforms. Zero friction.
              </h3>
              <p className="text-sm text-slate-400 max-w-sm leading-relaxed">
                Join 200,000+ authors already earning with openclaw.ai. Free plan includes 1 eBook, full export options, and distribution setup.
              </p>
            </div>

            {/* Right stats + CTA */}
            <div className="flex flex-col items-center gap-5 flex-shrink-0">
              <div className="grid grid-cols-3 gap-4 text-center">
                {[
                  { val: '8', sub: 'Platforms' },
                  { val: '$0', sub: 'To Start' },
                  { val: '100%', sub: 'Your Rights' },
                ].map(s => (
                  <div key={s.sub}>
                    <p className="text-2xl font-extrabold text-orange-400">{s.val}</p>
                    <p className="text-xs text-slate-500">{s.sub}</p>
                  </div>
                ))}
              </div>
              <button
                onClick={onSignUp}
                className="flex items-center gap-2.5 px-8 py-3.5 bg-gradient-to-r from-orange-500 to-amber-500 text-white font-extrabold text-sm rounded-2xl hover:opacity-90 transition-all cursor-pointer whitespace-nowrap shadow-xl shadow-orange-500/25"
              >
                <i className="ri-rocket-line text-base"></i>
                Publish Your eBook Free
                <i className="ri-arrow-right-line text-sm"></i>
              </button>
              <p className="text-xs text-slate-600">No credit card · Cancel anytime · Free plan forever</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
